import React, { useEffect, useState, useContext, useRef, Fragment } from "react";
import Header from "../Header/Header";
import GoLoading from "../Loading/GoLoading";
import client from "../../api/client";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../contextStore/AuthContext";
import "./Edit.css";

export default function EditMachine() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [machine, setMachine] = useState(null);

  // Dynamic industry options (fetched from API)
  const [industryOptions, setIndustryOptions] = useState([]);

  // Form fields
  const [title, setTitle] = useState("");
  const [industries, setIndustries] = useState([]);
  const [manuYear, setManuYear] = useState("");
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [contactNumber, setContactNumber] = useState("");

  // new fields
  const [make, setMake] = useState("");
  const [production, setProduction] = useState("");

  const [images, setImages] = useState([]);
  const [removedExistingImages, setRemovedExistingImages] = useState([]); // 👈 add this

  const fileInputRef = useRef();

  // Fetch categories (same style as Create.js)
  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await client.get("/api/categories");
        if (res.data?.ok && Array.isArray(res.data.categories)) {
          setIndustryOptions(res.data.categories.map((c) => c.name));
        }
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    }
    fetchCategories();
  }, []);

  // Load machine details
  useEffect(() => {
    async function loadMachine() {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await client.get(`/api/machines/${id}`, { headers });
        if (!res.data || !res.data.ok) throw new Error("Failed to fetch machine");

        const m = res.data.machine || {};
        setMachine(m);

        setTitle(m.title || "");
        setDescription((m.specification && m.specification.text) || m.description || "");
        setPrice(m.price || "");
        setCity(m.location_city || "");
        setPincode(m.pincode || m.postcode || "");
        setManuYear(m.manu_year || m.manufacturing_year || "");
        setModel(m.model || m.model_number || "");
        setContactNumber(m.seller_phone || m.contact_number || "");

        setMake(m.make || m.manufactured_by || "");
        setProduction(m.production || m.production_capacity || "");

        // industries: ensure we always end up with ONE selected industry (if exists)
        // industries: prefer industry_name from backend, fallback to old m.industries format
        if (m.industry_name) {
          setIndustries([String(m.industry_name)]);
        } else {
          try {
            const rawIndustries = m.industries;
            const parsed =
              typeof rawIndustries === "string" ? JSON.parse(rawIndustries) : rawIndustries;

            if (Array.isArray(parsed) && parsed.length > 0) {
              setIndustries([String(parsed[0])]); // only first category
            } else if (parsed) {
              setIndustries([String(parsed)]);
            } else {
              setIndustries([]);
            }
          } catch {
            setIndustries(m.industries ? [String(m.industries)] : []);
          }
        }


        // images: ensure absolute URLs for preview
        const imgs = (m.images || [])
          .map((img) => ({
            url:
              img.url && img.url.startsWith("http")
                ? img.url
                : img.url
                  ? `${window.location.origin}${img.url}`
                  : null,
            rawUrl: img.raw_url || img.url, // 👈 keep raw DB url

            existing: true,
          }))
          .filter((x) => x && x.url);
        setImages(imgs);
      } catch (err) {
        console.error("Error loading machine:", err);
        setError("Failed to load machine details.");
      } finally {
        setLoading(false);
      }
    }
    loadMachine();
  }, [id]);

  // SINGLE-SELECT industry
  function toggleIndustry(val) {
    setIndustries([val]); // always keep exactly ONE selected category
  }

  function handleFileInputChange(e) {
    const files = Array.from(e.target.files || []);
    const newFiles = files.map((file) => ({ file, url: URL.createObjectURL(file) }));
    setImages((prev) => [...prev, ...newFiles].slice(0, 12));
    e.target.value = "";
  }

  function removeImage(idx) {
    setImages((prev) => {
      const toRemove = prev[idx];

      // if it's an existing DB image, remember its raw URL to delete on backend
      if (toRemove && toRemove.existing && toRemove.rawUrl) {
        setRemovedExistingImages((prevRemoved) => [
          ...prevRemoved,
          toRemove.rawUrl,
        ]);
      }

      if (toRemove && toRemove.url && toRemove.file) {
        try {
          URL.revokeObjectURL(toRemove.url);
        } catch { }
      }

      return prev.filter((_, i) => i !== idx);
    });
  }


  async function handleUpdate(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!title.trim() || !price) {
      setError("Title and Price are required");
      return;
    }

    try {
      setSaving(true);

      const form = new FormData();
      form.append("title", title);
      form.append("description", description);
      form.append("specification", description); // backend accepts specification too
      form.append("price", price);
      form.append("currency", "INR");
      form.append("location_city", city);
      form.append("pincode", pincode);
      form.append("manu_year", manuYear);
      form.append("model", model);
      form.append("contact_number", contactNumber);

      const normalizedIndustries = Array.isArray(industries)
        ? industries.filter(Boolean)
        : [];
      form.append("industries", JSON.stringify(normalizedIndustries));

      // send primary industry like Create.js
      if (normalizedIndustries.length === 1) {
        form.append("industry_name", normalizedIndustries[0]);
      }

      if (make) form.append("make", make);
      if (production) form.append("production", production);
      if (removedExistingImages.length > 0) {
        form.append(
          "removed_images",
          JSON.stringify(removedExistingImages)
        );
      }

      // append only new local files (existing images are already on server)
      images.forEach((img) => {
        if (img.file) form.append("images", img.file);
      });

      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await client.put(`/api/machines/${id}`, form, {
        headers,
        timeout: 20000,
      });
      if (!res.data || res.data.ok === false) {
        const msg = res.data?.error || res.data?.message || "Update failed";
        throw new Error(msg);
      }

      // If backend returns the updated machine, use it to update UI immediately
      if (res.data.machine) {
        const m = res.data.machine;
        setMachine(m);
        setTitle(m.title || "");
        setDescription(m.description || "");
        setPrice(m.price || "");
        setCity(m.location_city || "");
        setPincode(m.pincode || "");
        setManuYear(m.manu_year || "");
        setModel(m.model || "");
        setMake(m.make || "");
        setProduction(m.production || "");

        try {
          const rawIndustries = m.industries;
          const parsed =
            typeof rawIndustries === "string" ? JSON.parse(rawIndustries) : rawIndustries;

          if (Array.isArray(parsed) && parsed.length > 0) {
            setIndustries([String(parsed[0])]);
          } else if (parsed) {
            setIndustries([String(parsed)]);
          } else {
            setIndustries([]);
          }
        } catch {
          setIndustries(m.industries ? [String(m.industries)] : []);
        }
      }

      setSuccess("Machine updated successfully!");
      setTimeout(() => navigate(`/machines/${id}`), 900);
    } catch (err) {
      console.error("Update error:", err);
      if (err.response && err.response.data) {
        setError(
          err.response.data?.error || err.response.data?.message || String(err)
        );
      } else {
        setError(err.message || "Failed to update machine.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <GoLoading />;

  return (
    <Fragment>
      <Header />
      <div className="edit-page container">
        <form onSubmit={handleUpdate} className="edit-card">
          <div className="left-col">
            <h2 className="page-title">Edit Machine</h2>
            <p className="subtitle">Update details for your listing.</p>

            <div className="field">
              <label className="field-label">Machine Name *</label>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div className="two-up">
              <div className="field">
                <label className="field-label">Manufactured By</label>
                <input
                  className="input"
                  value={make}
                  onChange={(e) => setMake(e.target.value)}
                  placeholder="e.g. JCB, Mahindra"
                />
              </div>

              <div className="field">
                <label className="field-label">Model Number</label>
                <input
                  className="input"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Model / Part no."
                />
              </div>
            </div>

            <div className="two-up">
              <div className="field">
                <label className="field-label">Manufacturing Year</label>
                <input
                  className="input"
                  type="number"
                  min="1900"
                  max="2100"
                  value={manuYear}
                  onChange={(e) => setManuYear(e.target.value)}
                  placeholder="YYYY"
                />
              </div>

              <div className="field">
                <label className="field-label">Production</label>
                <input
                  className="input"
                  value={production}
                  onChange={(e) => setProduction(e.target.value)}
                  placeholder="e.g. 50 kg/hr"
                />
              </div>
            </div>

            <div className="field">
              <label className="field-label">Industries</label>
              <div className="chips" role="list">
                {industryOptions.map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    className={`chip ${industries.includes(opt) ? "selected" : ""
                      }`}
                    onClick={() => toggleIndustry(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label className="field-label">Description</label>
              <textarea
                className="input textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="two-up">
              <div className="field">
                <label className="field-label">Price (INR)</label>
                <input
                  className="input"
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="field-label">City</label>
                <input
                  className="input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
            </div>

            <div className="two-up">
              <div className="field">
                <label className="field-label">Pincode</label>
                <input
                  className="input"
                  value={pincode}
                  onChange={(e) =>
                    setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                />
              </div>

              <div className="field">
                <label className="field-label">Contact Number</label>
                <input
                  className="input"
                  value={contactNumber}
                  onChange={(e) =>
                    setContactNumber(
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <div className="actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => navigate(-1)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn primary"
                disabled={saving}
              >
                {saving ? "Updating..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="right-col">
            <div className="card">
              <div className="thumb-header">Machine Photos</div>
              <div
                className="dropzone"
                onClick={() => fileInputRef.current?.click()}
              >
                <div>Click to add new images</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileInputChange}
                />
              </div>

              {images.length > 0 && (
                <div className="thumb-grid">
                  {images.map((img, i) => (
                    <div key={i} className="thumb">
                      <img src={img.url} alt={`Preview ${i + 1}`} />
                      <button
                        type="button"
                        className="remove"
                        onClick={() => removeImage(i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="muted small" style={{ marginTop: 8 }}>
                JPG, PNG, WEBP • Max 5MB each
              </div>
            </div>
          </div>
        </form>
      </div>
    </Fragment>
  );
}
